import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NavigationStart, Router} from '@angular/router';
import {IonicModule} from '@ionic/angular';
import {TranslateModule} from '@ngx-translate/core';
import {filter, Subscription} from 'rxjs';
import {Haptics, ImpactStyle} from '@capacitor/haptics';
import {SmNavMenuItemModel} from '../components/sm-nav-menu-item/sm-nav-menu-item.component';
import {QuickNavigationPreferencesService} from './quick-navigation-preferences.service';
import {SmModalStackService} from '../components/sm-confirmation-dialog/sm-modal-stack.service';
import {clampPosition, DEFAULT_QUICK_NAVIGATION_POSITION, isHorizontalEdge, navigationHandlePoint, navigationPanelPoint, nearestNavigationPosition, NavigationBounds, NavigationPoint, QuickNavigationPosition} from './quick-navigation-position';

@Component({
  selector: 'sm-quick-navigation',
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule],
  templateUrl: './quick-navigation.component.html',
  styleUrls: ['./quick-navigation.component.scss'],
})
export class QuickNavigationComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() accountId = '';
  @Input() items: SmNavMenuItemModel[] = [];
  @Input() selectedId = '';
  @Input() blocked = false;
  @ViewChild('bounds') boundsElement?: ElementRef<HTMLElement>;
  @ViewChild('panelBounds') panelBoundsElement?: ElementRef<HTMLElement>;
  @ViewChild('handle') handleElement?: ElementRef<HTMLButtonElement>;
  @ViewChild('panel') panelElement?: ElementRef<HTMLDialogElement>;

  position: QuickNavigationPosition = {...DEFAULT_QUICK_NAVIGATION_POSITION};
  handlePoint: NavigationPoint = {x: 0, y: 0};
  panelPoint: NavigationPoint = {x: 0, y: 0};
  ready = false;
  open = false;
  dragging = false;
  keyboardVisible = false;
  transparency = 90;
  private readonly preferences = inject(QuickNavigationPreferencesService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly modalStack = inject(SmModalStackService);
  private readonly routeSubscription: Subscription;
  private readonly transparencySubscription: Subscription;
  private resizeObserver?: ResizeObserver;
  private pressTimer?: ReturnType<typeof setTimeout>;
  private pointer?: {id: number; start: NavigationPoint; position: QuickNavigationPosition};
  private suppressClick = false;
  private restoration = 0;
  private destroyed = false;
  private focusTimer?: ReturnType<typeof setTimeout>;
  private readonly viewportChanged = () => this.handleResize();

  constructor() {
    this.transparencySubscription = this.preferences.transparencyChanges.subscribe(change => {
      if (change.accountId !== this.accountId) return;
      this.transparency = change.value;
      this.cdr.markForCheck();
    });
    this.routeSubscription = this.router.events.pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => this.closePanel(false));
  }

  get horizontal(): boolean { return isHorizontalEdge(this.position.edge); }
  get visible(): boolean { return this.ready && !!this.accountId && this.items.length > 0; }
  get chevron(): string {
    return {top: 'chevron-down-outline', bottom: 'chevron-up-outline', left: 'chevron-forward-outline', right: 'chevron-back-outline'}[this.position.edge];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['accountId']) void this.restorePosition();
    if (changes['blocked'] || changes['items']) {
      this.cancelPointer();
      this.closePanel(false);
    }
  }

  ngAfterViewInit(): void {
    if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.boundsElement!.nativeElement);
    window.visualViewport?.addEventListener('resize', this.viewportChanged);
    this.handleResize();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.restoration++;
    clearTimeout(this.focusTimer);
    this.cancelPointer();
    this.closePanel(false);
    this.resizeObserver?.disconnect();
    if (typeof window !== 'undefined') window.visualViewport?.removeEventListener('resize', this.viewportChanged);
    this.routeSubscription.unsubscribe();
    this.transparencySubscription.unsubscribe();
  }

  async restorePosition(): Promise<void> {
    const generation = ++this.restoration;
    this.ready = false;
    this.cancelPointer();
    this.closePanel(false);
    const [position, transparency] = await Promise.all([
      this.preferences.read(this.accountId), this.preferences.readTransparency(this.accountId),
    ]);
    if (generation !== this.restoration || this.destroyed) return;
    this.position = position ?? {...DEFAULT_QUICK_NAVIGATION_POSITION};
    this.transparency = transparency;
    this.ready = true;
    this.handleResize();
  }

  @HostListener('window:resize')
  handleResize(): void {
    if (this.destroyed || !this.boundsElement || typeof window === 'undefined') return;
    this.keyboardVisible = !!document.activeElement?.matches('input, textarea, [contenteditable="true"]')
      || (!!window.visualViewport && window.innerHeight - window.visualViewport.height > 120);
    if (this.keyboardVisible) this.closePanel(false);
    this.handlePoint = navigationHandlePoint(this.position, this.bounds(), this.handleDepth());
    this.placePanel();
    this.cdr.detectChanges();
  }

  @HostListener('document:focusin')
  @HostListener('document:focusout')
  handleFocusChange(): void {
    clearTimeout(this.focusTimer);
    this.focusTimer = setTimeout(() => this.handleResize());
  }

  onPointerDown(event: PointerEvent): void {
    if (!event.isPrimary || event.button !== 0) return;
    event.stopPropagation();
    this.suppressClick = false;
    this.pointer = {id: event.pointerId, start: {x: event.clientX, y: event.clientY}, position: {...this.position}};
    this.handleElement?.nativeElement.setPointerCapture(event.pointerId);
    this.pressTimer = setTimeout(() => {
      this.dragging = true;
      this.suppressClick = true;
      this.closePanel(false);
      void Haptics.impact({style: ImpactStyle.Light}).catch(() => undefined);
      this.cdr.detectChanges();
    }, 450);
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.pointer || event.pointerId !== this.pointer.id) return;
    event.stopPropagation();
    const point = {x: event.clientX, y: event.clientY};
    if (this.dragging) {
      this.position = nearestNavigationPosition(point, this.bounds());
      this.handlePoint = navigationHandlePoint(this.position, this.bounds(), this.handleDepth());
      return;
    }
    if (Math.hypot(point.x - this.pointer.start.x, point.y - this.pointer.start.y) > 10) {
      clearTimeout(this.pressTimer);
      this.suppressClick = true;
    }
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.pointer || event.pointerId !== this.pointer.id) return;
    event.stopPropagation();
    const dx = event.clientX - this.pointer.start.x;
    const dy = event.clientY - this.pointer.start.y;
    const inward = {top: dy, bottom: -dy, left: dx, right: -dx}[this.position.edge];
    if (this.dragging) void this.preferences.write(this.accountId, {...this.position});
    else if (inward > 24) this.openPanel();
    this.finishPointer();
  }

  cancelPointer(): void {
    if (this.pointer) this.position = this.pointer.position;
    this.finishPointer();
    if (this.boundsElement && typeof window !== 'undefined') this.handlePoint = navigationHandlePoint(this.position, this.bounds(), this.handleDepth());
  }

  onHandleClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.suppressClick && event.detail !== 0) { this.suppressClick = false; return; }
    this.openPanel();
  }

  onHandleKey(event: KeyboardEvent): void {
    if (!event.altKey || !event.key.startsWith('Arrow')) return;
    event.preventDefault();
    const edge = {ArrowUp: 'top', ArrowDown: 'bottom', ArrowLeft: 'left', ArrowRight: 'right'}[event.key] as QuickNavigationPosition['edge'];
    this.position = {edge, ratio: this.position.ratio};
    this.handleResize();
    void this.preferences.write(this.accountId, this.position);
  }

  openPanel(): void {
    if (!this.visible || this.blocked || this.dragging) return;
    if (document.querySelector('dialog[open], ion-modal:not(.overlay-hidden), ion-alert:not(.overlay-hidden)')) return;
    const panel = this.panelElement?.nativeElement;
    if (!panel) return;
    this.open = true;
    this.cdr.detectChanges();
    panel.showModal();
    this.modalStack.opened(panel);
    this.placePanel();
    this.cdr.detectChanges();
    panel.querySelector<HTMLElement>('a')?.focus({preventScroll: true});
  }

  closePanel(restoreFocus = true): void {
    const wasOpen = this.open;
    this.open = false;
    if (this.panelElement?.nativeElement) this.modalStack.closed(this.panelElement.nativeElement);
    this.panelElement?.nativeElement.close();
    if (wasOpen && restoreFocus) {
      this.cdr.detectChanges();
      this.handleElement?.nativeElement.focus({preventScroll: true});
    }
  }

  onCancel(event: Event): void { event.preventDefault(); this.closePanel(); }

  onPanelClick(event: MouseEvent): void {
    if (!(event.target instanceof Element) || event.target.closest('a')) return;
    this.closePanel();
  }

  onNavigate(event: MouseEvent, item: SmNavMenuItemModel): void {
    event.preventDefault();
    this.closePanel(false);
    const currentPath = this.router.url.split(/[?#]/)[0];
    if (item.routeHref && item.routeHref !== currentPath) void this.router.navigateByUrl(item.routeHref);
  }

  private finishPointer(): void {
    clearTimeout(this.pressTimer);
    const handle = this.handleElement?.nativeElement;
    const pointerId = this.pointer?.id;
    this.pointer = undefined;
    this.dragging = false;
    if (pointerId !== undefined && handle?.hasPointerCapture(pointerId)) handle.releasePointerCapture(pointerId);
  }

  private handleDepth(): number {
    return parseFloat(getComputedStyle(this.boundsElement!.nativeElement).getPropertyValue('--quick-navigation-handle-depth')) || 44;
  }

  private bounds(): NavigationBounds {
    const rect = this.boundsElement!.nativeElement.getBoundingClientRect();
    return {left: rect.left, top: rect.top, width: Math.max(56, rect.width), height: Math.max(56, rect.height)};
  }

  private placePanel(): void {
    if (!this.open || !this.panelElement) return;
    const panel = this.panelElement.nativeElement;
    const rect = this.panelBoundsElement!.nativeElement.getBoundingClientRect();
    const bounds = {left: rect.left, top: rect.top, width: rect.width, height: rect.height};
    panel.style.maxWidth = `${clampPosition(bounds.width, 0, 360)}px`;
    panel.style.maxHeight = `${bounds.height}px`;
    this.panelPoint = navigationPanelPoint(this.position, bounds, panel.getBoundingClientRect());
  }
}
